type DecodedTokenPayload = {
  exp?: number;
  sessionExp?: number | string;
  [key: string]: unknown;
};

const encode = (value: string): string => btoa(unescape(encodeURIComponent(value)));
const decode = (value: string): string => decodeURIComponent(escape(atob(value)));

const STORAGE_KEYS = {
  TOKEN: "maleva_auth_token",
  USER: "maleva_auth_user",
  LAST_ACTIVITY: "maleva_last_activity_at",
} as const;

const readTokenValue = (): string | null =>
  localStorage.getItem(STORAGE_KEYS.TOKEN) ??
  sessionStorage.getItem(STORAGE_KEYS.TOKEN) ??
  localStorage.getItem("token") ??
  sessionStorage.getItem("token");

export const tokenHandler = {
  get(): string | null {
    const storedToken = readTokenValue();
    if (!storedToken) {
      return null;
    }

    try {
      return decode(storedToken);
    } catch {
      return storedToken;
    }
  },

  set(token: string, remember = false): void {
    const encodedToken = encode(token);

    if (remember) {
      localStorage.setItem(STORAGE_KEYS.TOKEN, encodedToken);
      sessionStorage.removeItem(STORAGE_KEYS.TOKEN);
    } else {
      sessionStorage.setItem(STORAGE_KEYS.TOKEN, encodedToken);
      localStorage.removeItem(STORAGE_KEYS.TOKEN);
    }

    tokenHandler.touchActivity();
  },

  clear(): void {
    localStorage.removeItem(STORAGE_KEYS.TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER);
    localStorage.removeItem(STORAGE_KEYS.LAST_ACTIVITY);
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    sessionStorage.removeItem(STORAGE_KEYS.TOKEN);
    sessionStorage.removeItem(STORAGE_KEYS.USER);
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("user");
  },

  isValid(bufferMinutes = 5): boolean {
    const payload = tokenHandler.decodePayload();
    if (!payload?.exp) {
      return false;
    }

    const bufferMs = bufferMinutes * 60 * 1000;
    return payload.exp * 1000 - bufferMs > Date.now();
  },

  decodePayload(token: string | null = tokenHandler.get()): DecodedTokenPayload | null {
    if (!token) {
      return null;
    }

    try {
      const base64 = token.split(".")[1];
      if (!base64) {
        return null;
      }

      const normalized = base64.replace(/-/g, "+").replace(/_/g, "/");
      const padding = "=".repeat((4 - (normalized.length % 4)) % 4);
      return JSON.parse(atob(normalized + padding)) as DecodedTokenPayload;
    } catch {
      return null;
    }
  },

  handleExpired(onExpired?: () => void): void {
    tokenHandler.clear();
    onExpired?.();
  },

  touchActivity(): void {
    localStorage.setItem(STORAGE_KEYS.LAST_ACTIVITY, Date.now().toString());
  },

  getLastActivityAt(): number | null {
    const value = localStorage.getItem(STORAGE_KEYS.LAST_ACTIVITY);
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  },

  hasExceededIdleTimeout(timeoutMs: number): boolean {
    const lastActivityAt = tokenHandler.getLastActivityAt();
    if (!lastActivityAt) {
      return false;
    }

    return Date.now() - lastActivityAt >= timeoutMs;
  },

  getSessionExpiresAt(token: string | null = tokenHandler.get()): number | null {
    const payload = tokenHandler.decodePayload(token);
    if (!payload) {
      return null;
    }

    if (payload.sessionExp) {
      const parsed = Number(payload.sessionExp);
      return Number.isFinite(parsed) ? parsed : null;
    }

    if (payload.exp) {
      return payload.exp * 1000;
    }

    return null;
  },

  hasExceededSessionMaxAge(token: string | null = tokenHandler.get()): boolean {
    const sessionExpiresAt = tokenHandler.getSessionExpiresAt(token);
    if (!sessionExpiresAt) {
      return false;
    }

    return Date.now() >= sessionExpiresAt;
  },
};
